/**
 * Starred — today's hit list (§8.2's second section, §8.3's "always visible regardless").
 * Self-contained the way `TasksCard` is, and shares its hold-in-place action wiring
 * (`useHeldTaskActions`) since a starred task can be completed, undone, deferred, edited,
 * dropped or deleted exactly the same as an open one — the only difference is where the
 * list comes from and that this one ignores the energy slider entirely.
 *
 * §8.2 lists every starred task, not the first three — see `services/briefing.py:starred_tasks`
 * for why; the "top 3" in this file's own header is the docstring's, not a cap this enforces.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Task } from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { useHeldTaskActions } from "../useHeldTaskActions";
import { useFireworks } from "./fireworksContext";
import { TaskList } from "./TaskList";
import { todayLocalDate } from "./recurrence";

export function StarredCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed a task, so this list stays current. */
  refreshKey?: number;
  /** Called after this card mutates anything, so siblings relying on the same tasks (Wins,
   * Gone-quiet, Tasks) know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects } = useAreasAndProjects(refreshKey);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [error, setError] = useState<string | null>(null);
  const burst = useFireworks();

  const load = useCallback(async () => {
    try {
      setTasks((await api.starredTasks()).tasks);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const { actions, withHeld } = useHeldTaskActions({ reload: load, onChange, burst });

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const shown = withHeld(tasks);
  const clear = shown.length === 0 && !error;

  return (
    <section className="panel">
      <header>
        <h2>Starred</h2>
        <span className="muted small">Today's hit list</span>
      </header>

      {error && <p className="error">{error}</p>}

      {clear ? (
        <div className="empty">
          <strong>Nothing starred - the deck is clear.</strong>
          <p style={{ margin: "0.5rem 0 0" }}>
            Browse the Tasks list and star a few for today, whenever you're ready to pick.
          </p>
        </div>
      ) : (
        <TaskList
          tasks={shown}
          today={todayLocalDate()}
          areas={areas}
          projects={projects}
          actions={actions}
        />
      )}
    </section>
  );
}
